/**
 * CopilotTierPanel — the line in the sand. Shows what the free assistant does,
 * then a clear divider, then what Copilot Pro does and who may use it. Pro is
 * super-admins only (enforced server-side by core/rbac_hierarchy.py).
 */
import { Check, Lock, Sparkles, Crown } from 'lucide-react';

export const CopilotTierPanel = ({ tier, compact = false }) => {
  if (!tier) return null;
  const pro = tier.tier === 'pro';

  return (
    <div className={compact ? 'px-4 py-3 space-y-3' : 'p-4 space-y-4'}
         data-testid="copilot-tier-panel">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-violet-700
                      flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" /> Copilot Free · included for everyone
        </p>
        <ul className="mt-2 space-y-1">
          {(tier.free_features || []).map((f) => (
            <li key={f} className="text-xs text-slate-600 flex gap-2">
              <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="relative py-1" data-testid="copilot-tier-divider">
        <div className="border-t border-dashed border-slate-300" />
        <span className="absolute -top-[7px] left-1/2 -translate-x-1/2 bg-white px-2
                         text-[10px] uppercase tracking-[0.18em] text-slate-400">
          free ends here
        </span>
      </div>

      <div className={pro ? '' : 'opacity-95'}>
        <p className={`text-[10px] font-semibold uppercase tracking-[0.16em] flex items-center gap-1.5
                       ${pro ? 'text-amber-700' : 'text-slate-500'}`}>
          {pro ? <Crown className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
          Copilot Pro · {pro ? 'unlocked for you' : 'super-admins only'}
        </p>
        <p className="mt-1.5 text-xs text-slate-500">{tier.pro_blurb}</p>
        <ul className="mt-2 space-y-1">
          {(tier.pro_features || []).map((f) => (
            <li key={f} className="text-xs flex gap-2">
              {pro
                ? <Check className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
                : <Lock className="h-3 w-3 text-slate-300 shrink-0 mt-[3px]" />}
              <span className={pro ? 'text-slate-600' : 'text-slate-400'}>{f}</span>
            </li>
          ))}
        </ul>
        {!pro && (
          <p className="mt-2 text-[11px] text-slate-400" data-testid="copilot-pro-locked-note">
            You are signed in as {tier.role_label}. Ask a super-admin to run anything on this list.
          </p>
        )}
      </div>
    </div>
  );
};

export default CopilotTierPanel;
